import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  // Zoho-style primary
  type: "business",
  salutation: "",
  first_name: "",
  last_name: "",
  company_name: "",
  display_name: "",
  name: "",
  email: "",
  phone: "",
  alt_phone: "",
  customer_language: "English",
  communication_channels: ["email", "sms"],
  // Other details tab
  gst_treatment: "",
  place_of_supply: "",
  gstin: "",
  pan: "",
  cin: "",
  tax_preference: "taxable",
  currency: "INR",
  opening_balance: 0,
  opening_balance_type: "credit",
  payment_terms: "due_on_receipt",
  customer_owner: "",
  portal_enabled: false,
  // Address tab
  billing_address: "",
  shipping_address: "",
  city: "",
  state: "",
  pincode: "",
  // Contact person tab
  contact_person: "",
  contact_designation: "",
  alt_email: "",
  // Others
  industry: "",
  website: "",
  reporting_tags: [],
  remarks: "",
  // Commercial
  credit_limit: 100000,
  credit_days: 30,
  is_postpaid: false,
  wallet_balance: 0,
  price_per_litre: 95.0,
};

const GST_TREATMENTS = [
  { v: "registered_regular", l: "Registered Business - Regular" },
  { v: "registered_composition", l: "Registered Business - Composition" },
  { v: "unregistered", l: "Unregistered Business" },
  { v: "consumer", l: "Consumer" },
  { v: "overseas", l: "Overseas" },
  { v: "sez", l: "Special Economic Zone" },
  { v: "deemed_export", l: "Deemed Export" },
  { v: "tax_deductor", l: "Tax Deductor" },
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Lakshadweep", "Puducherry",
];

const PAYMENT_TERMS = [
  { v: "due_on_receipt", l: "Due on Receipt" },
  { v: "net_15", l: "Net 15" },
  { v: "net_30", l: "Net 30" },
  { v: "net_45", l: "Net 45" },
  { v: "net_60", l: "Net 60" },
  { v: "net_90", l: "Net 90" },
];

export default function Customers() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["admin", "accounts", "manager"].includes(user?.role);

  const [q, setQ] = useState("");
  const [editor, setEditor] = useState(null); // {mode, cust}
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["customers", q],
    queryFn: () => api.get("/customers", { params: { q, limit: 200 } }).then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: (p) => api.post("/customers", p),
    onSuccess: () => { toast.success("Customer created"); setEditor(null); qc.invalidateQueries({ queryKey: ["customers"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/customers/${id}`, patch),
    onSuccess: () => { toast.success("Customer updated"); setEditor(null); qc.invalidateQueries({ queryKey: ["customers"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  const remove = useMutation({
    mutationFn: ({ id, force }) => api.delete(`/customers/${id}`, { params: force ? { force: true } : {} }),
    onSuccess: () => { toast.success("Customer deleted"); setConfirmDelete(null); qc.invalidateQueries({ queryKey: ["customers"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const openCreate = () => setEditor({ mode: "create", cust: { ...EMPTY } });
  const openEdit = (c) =>
    setEditor({
      mode: "edit",
      cust: {
        id: c.id,
        ...EMPTY,
        ...Object.fromEntries(Object.entries(c).filter(([k]) => k in EMPTY || ["id"].includes(k))),
      },
    });

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground">{data?.total || 0} accounts</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            placeholder="Search name / GST / CIN / phone / contact"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full sm:w-72"
            data-testid="customers-search"
          />
          <Button onClick={openCreate} data-testid="btn-new-customer" className="whitespace-nowrap">
            <Plus size={14} className="mr-1" /> New customer
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">GSTIN · CIN</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3 text-right">Credit</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3">KYC</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</td></tr>
              )}
              {(data?.items || []).map((c) => (
                <tr key={c.id} className="border-b border-border/40 text-sm hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono font-semibold">
                    <Link to={`/app/customers/${c.id}`} className="hover:text-primary" data-testid={`customer-${c.code}`}>{c.code}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{c.name}</div>
                    {c.industry && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.industry}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {c.contact_person && <div className="font-semibold">{c.contact_person}</div>}
                    <div className="text-muted-foreground">{c.phone}</div>
                    {c.email && <div className="text-muted-foreground">{c.email}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div>{c.gstin || "—"}</div>
                    {c.cin && <div className="text-muted-foreground">{c.cin}</div>}
                  </td>
                  <td className="px-4 py-3">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{(c.credit_limit || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{(c.outstanding || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.kyc_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Edit"
                        data-testid={`cust-edit-${c.code}`}
                      >
                        <Pencil size={14} />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => setConfirmDelete(c)}
                          className="rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600"
                          title="Delete"
                          data-testid={`cust-del-${c.code}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (!data?.items || data.items.length === 0) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">No customers.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <CustomerEditor
        open={!!editor}
        editor={editor}
        onClose={() => setEditor(null)}
        onSave={(form) => {
          if (editor.mode === "create") create.mutate(form);
          else update.mutate({ id: editor.cust.id, patch: form });
        }}
        saving={create.isPending || update.isPending}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.name}</b> ({confirmDelete?.code}) will be permanently removed. All KYC documents on disk will be unlinked.
              If the customer has outstanding balance or orders, deletion will be blocked unless you check &ldquo;Force delete&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <input
              type="checkbox"
              id="force-del"
              className="h-4 w-4"
              onChange={(e) => setConfirmDelete((d) => ({ ...d, _force: e.target.checked }))}
              data-testid="cust-force-delete"
            />
            <label htmlFor="force-del">Force delete (ignore outstanding balance / order history)</label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove.mutate({ id: confirmDelete.id, force: !!confirmDelete._force })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="cust-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CustomerEditor({ open, editor, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState("other");

  React.useEffect(() => {
    if (editor?.cust) {
      setForm({ ...EMPTY, ...editor.cust });
      setTab("other");
      // If editing an existing customer whose display_name doesn't match any
      // auto-computed option, keep them in "custom" mode so nothing overwrites.
      const existing = (editor.cust.display_name || "").trim();
      prevAutoRef.current = existing;
      setDisplayMode(existing ? "auto" : "auto");
    }
  }, [editor]);

  // Zoho-style Display Name auto-suggest:
  // As soon as first/last/company change, offer a dropdown of computed
  // combinations. User can pick one or switch to "Custom" to type freely.
  const displayOptions = React.useMemo(() => {
    const s = (form.salutation || "").trim();
    const f = (form.first_name || "").trim();
    const l = (form.last_name || "").trim();
    const c = (form.company_name || "").trim();
    const person = [s, f, l].filter(Boolean).join(" ");
    const lastFirst = [l, f].filter(Boolean).join(", ");
    const set = new Set();
    if (person) set.add(person);
    if (lastFirst && lastFirst !== person) set.add(lastFirst);
    if (c) set.add(c);
    if (c && person) set.add(`${person} — ${c}`);
    if (c && person) set.add(`${c} (${person})`);
    // Always include the currently-selected value so the Select doesn't
    // appear blank when editing a customer with a non-standard display_name.
    const current = (form.display_name || "").trim();
    if (current) set.add(current);
    return [...set];
  }, [form.salutation, form.first_name, form.last_name, form.company_name, form.display_name]);

  // Track whether the user has manually typed a custom value. Once they do,
  // stop auto-populating so we don't stomp their edit.
  const [displayMode, setDisplayMode] = React.useState("auto"); // "auto" | "custom"
  const prevAutoRef = React.useRef("");

  React.useEffect(() => {
    if (displayMode !== "auto") return;
    // Default to first computed option; if the current display_name still
    // matches the previous auto-fill (i.e. user hasn't hand-edited), refresh.
    const next = displayOptions[0] || "";
    if (!next) return;
    if (form.display_name && form.display_name !== prevAutoRef.current) return;
    if (form.display_name === next) return;
    prevAutoRef.current = next;
    setForm((f) => ({ ...f, display_name: next }));
  }, [displayOptions, displayMode, form.display_name]);

  if (!editor) return null;

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (ch) =>
    setForm((f) => ({
      ...f,
      communication_channels: (f.communication_channels || []).includes(ch)
        ? f.communication_channels.filter((c) => c !== ch)
        : [...(f.communication_channels || []), ch],
    }));

  const submit = () => {
    // Compute effective name/company_name/display_name for backend
    const display = (form.display_name || "").trim();
    const company = (form.company_name || "").trim();
    const fullPerson = `${form.first_name || ""} ${form.last_name || ""}`.trim();
    const effectiveName = display || company || fullPerson;
    if (!effectiveName) {
      toast.error("Display name (or Company / Person name) is required");
      return;
    }
    if (!form.phone || !form.phone.trim()) {
      toast.error("Phone is required");
      return;
    }
    const payload = { ...form };
    payload.name = effectiveName;
    payload.display_name = display || effectiveName;
    payload.contact_person = payload.contact_person || fullPerson;
    delete payload.id;
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto p-0" data-testid="cust-editor-form">
        <DialogHeader className="border-b border-border/60 px-6 py-4">
          <DialogTitle className="text-xl">
            {editor.mode === "create" ? "New customer" : `Edit ${editor.cust.name}`}
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Prefill customer details from the GST portal using the customer&apos;s GSTIN.
            <button
              type="button"
              className="ml-2 font-semibold text-primary hover:underline"
              onClick={() => toast.info("GSTIN prefill coming soon — configure your GST portal credentials in Settings.")}
              data-testid="btn-prefill-gstin"
            >
              Prefill →
            </button>
          </div>
        </DialogHeader>

        {/* Primary contact block */}
        <div className="space-y-5 px-6 py-5">
          <Row label="Customer Type">
            <div className="flex gap-6" data-testid="cust-type-radio">
              {[
                { v: "business", l: "Business" },
                { v: "individual", l: "Individual" },
              ].map((t) => (
                <label key={t.v} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="ctype"
                    value={t.v}
                    checked={form.type === t.v}
                    onChange={() => setVal("type", t.v)}
                    className="h-4 w-4 accent-primary"
                  />
                  {t.l}
                </label>
              ))}
            </div>
          </Row>

          <Row label="Primary Contact">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-6">
              <Select value={form.salutation || "none"} onValueChange={(v) => setVal("salutation", v === "none" ? "" : v)}>
                <SelectTrigger className="md:col-span-1" data-testid="cust-salutation"><SelectValue placeholder="Salutation" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="Mr.">Mr.</SelectItem>
                  <SelectItem value="Mrs.">Mrs.</SelectItem>
                  <SelectItem value="Ms.">Ms.</SelectItem>
                  <SelectItem value="Dr.">Dr.</SelectItem>
                  <SelectItem value="Prof.">Prof.</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="First Name"
                value={form.first_name}
                onChange={set("first_name")}
                className="md:col-span-2"
                data-testid="cust-first-name"
              />
              <Input
                placeholder="Last Name"
                value={form.last_name}
                onChange={set("last_name")}
                className="md:col-span-3"
                data-testid="cust-last-name"
              />
            </div>
          </Row>

          {form.type === "business" && (
            <Row label="Company Name">
              <Input value={form.company_name} onChange={set("company_name")} data-testid="cust-company-name" />
            </Row>
          )}

          <Row label="Display Name" required>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <Select
                value={displayMode === "custom" ? "__custom__" : (form.display_name || "__auto__")}
                onValueChange={(v) => {
                  if (v === "__custom__") {
                    setDisplayMode("custom");
                    return;
                  }
                  setDisplayMode("auto");
                  setVal("display_name", v);
                  prevAutoRef.current = v;
                }}
              >
                <SelectTrigger className="md:col-span-2" data-testid="cust-display-select">
                  <SelectValue placeholder="Select or type to add" />
                </SelectTrigger>
                <SelectContent>
                  {displayOptions.length === 0 && (
                    <SelectItem value="__auto__" disabled>
                      Enter first / last / company above
                    </SelectItem>
                  )}
                  {displayOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {displayMode === "custom" && (
                <Input
                  placeholder="Enter custom display name"
                  value={form.display_name}
                  onChange={set("display_name")}
                  data-testid="cust-display-name-custom"
                  autoFocus
                />
              )}
            </div>
          </Row>

          <Row label="Email Address">
            <Input
              type="email"
              value={form.email}
              onChange={set("email")}
              data-testid="cust-email"
              placeholder="name@example.com"
            />
          </Row>

          <Row label="Phone" required>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div className="flex gap-2">
                <span className="grid w-14 place-items-center rounded-md border border-input bg-muted text-sm">+91</span>
                <Input placeholder="Work Phone" value={form.phone} onChange={set("phone")} data-testid="cust-phone" />
              </div>
              <div className="flex gap-2">
                <span className="grid w-14 place-items-center rounded-md border border-input bg-muted text-sm">+91</span>
                <Input placeholder="Mobile" value={form.alt_phone} onChange={set("alt_phone")} data-testid="cust-mobile" />
              </div>
            </div>
          </Row>

          <Row label="Customer Language">
            <Select value={form.customer_language || "English"} onValueChange={(v) => setVal("customer_language", v)}>
              <SelectTrigger className="md:w-64" data-testid="cust-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali"].map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>

          <Row label="Communication Channels">
            <div className="flex flex-wrap gap-4">
              {[
                { v: "email", l: "Email" },
                { v: "sms", l: "SMS" },
                { v: "whatsapp", l: "WhatsApp" },
              ].map((c) => (
                <label key={c.v} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(form.communication_channels || []).includes(c.v)}
                    onChange={() => toggleChannel(c.v)}
                    className="h-4 w-4 accent-primary"
                    data-testid={`cust-comm-${c.v}`}
                  />
                  {c.l}
                </label>
              ))}
            </div>
          </Row>
        </div>

        {/* Tabs */}
        <div className="border-t border-border/60">
          <div className="flex gap-1 overflow-x-auto border-b border-border/60 bg-muted/30 px-6">
            {[
              { v: "other", l: "Other Details" },
              { v: "address", l: "Address" },
              { v: "contacts", l: "Contact Persons" },
              { v: "commercial", l: "Commercial" },
              { v: "tags", l: "Reporting Tags" },
              { v: "remarks", l: "Remarks" },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTab(t.v)}
                className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.v
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${t.v}`}
              >
                {t.l}
              </button>
            ))}
          </div>

          <div className="space-y-5 px-6 py-6">
            {tab === "other" && (
              <>
                <Row label="GST Treatment" required>
                  <Select value={form.gst_treatment || ""} onValueChange={(v) => setVal("gst_treatment", v)}>
                    <SelectTrigger className="md:w-96" data-testid="cust-gst-treatment"><SelectValue placeholder="Select a GST treatment" /></SelectTrigger>
                    <SelectContent>
                      {GST_TREATMENTS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Row>
                {form.gst_treatment && !["consumer", "overseas"].includes(form.gst_treatment) && (
                  <Row label="GSTIN">
                    <Input value={form.gstin} onChange={set("gstin")} className="md:w-96 font-mono uppercase" placeholder="15-digit GSTIN" data-testid="cust-gstin" />
                  </Row>
                )}
                <Row label="Place of Supply" required>
                  <Select value={form.place_of_supply || ""} onValueChange={(v) => setVal("place_of_supply", v)}>
                    <SelectTrigger className="md:w-96" data-testid="cust-pos"><SelectValue placeholder="Select a state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="PAN">
                  <Input value={form.pan} onChange={set("pan")} className="md:w-96 font-mono uppercase" placeholder="10-character PAN" data-testid="cust-pan" />
                </Row>
                <Row label="CIN">
                  <Input value={form.cin} onChange={set("cin")} className="md:w-96 font-mono uppercase" placeholder="U74999MH2020PTC123456" data-testid="cust-cin" />
                </Row>
                <Row label="Tax Preference" required>
                  <div className="flex gap-6">
                    {[
                      { v: "taxable", l: "Taxable" },
                      { v: "tax_exempt", l: "Tax Exempt" },
                    ].map((t) => (
                      <label key={t.v} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="tax_pref"
                          value={t.v}
                          checked={form.tax_preference === t.v}
                          onChange={() => setVal("tax_preference", t.v)}
                          className="h-4 w-4 accent-primary"
                          data-testid={`cust-tax-${t.v}`}
                        />
                        {t.l}
                      </label>
                    ))}
                  </div>
                </Row>
                <Row label="Currency">
                  <Select value={form.currency || "INR"} onValueChange={(v) => setVal("currency", v)}>
                    <SelectTrigger className="md:w-96" data-testid="cust-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                      <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Opening Balance">
                  <div className="flex gap-2 md:w-96">
                    <Select value={form.opening_balance_type || "credit"} onValueChange={(v) => setVal("opening_balance_type", v)}>
                      <SelectTrigger className="w-32" data-testid="cust-ob-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">Credit</SelectItem>
                        <SelectItem value="debit">Debit</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-input px-2">
                      <span className="text-xs text-muted-foreground">{form.currency || "INR"}</span>
                      <Input
                        type="number"
                        value={form.opening_balance}
                        onChange={set("opening_balance")}
                        className="border-0 focus-visible:ring-0"
                        data-testid="cust-opening-balance"
                      />
                    </div>
                  </div>
                </Row>
                <Row label="Payment Terms">
                  <Select value={form.payment_terms || "due_on_receipt"} onValueChange={(v) => setVal("payment_terms", v)}>
                    <SelectTrigger className="md:w-96" data-testid="cust-payment-terms"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Customer Owner">
                  <Input value={form.customer_owner} onChange={set("customer_owner")} className="md:w-96" placeholder="Select user" data-testid="cust-owner" />
                </Row>
                <Row label="Enable Portal?">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.portal_enabled}
                      onChange={(e) => setVal("portal_enabled", e.target.checked)}
                      className="h-4 w-4 accent-primary"
                      data-testid="cust-portal-enabled"
                    />
                    Allow portal access for this customer
                  </label>
                </Row>
              </>
            )}

            {tab === "address" && (
              <>
                <Row label="Billing Address">
                  <Textarea rows={3} value={form.billing_address} onChange={set("billing_address")} className="md:w-[32rem]" data-testid="cust-billing" />
                </Row>
                <Row label="Shipping Address">
                  <Textarea rows={3} value={form.shipping_address} onChange={set("shipping_address")} placeholder="Leave blank if same as billing" className="md:w-[32rem]" data-testid="cust-shipping" />
                </Row>
                <Row label="City / State / Pincode">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:w-[32rem]">
                    <Input placeholder="City" value={form.city} onChange={set("city")} />
                    <Input placeholder="State" value={form.state} onChange={set("state")} />
                    <Input placeholder="Pincode" value={form.pincode} onChange={set("pincode")} />
                  </div>
                </Row>
              </>
            )}

            {tab === "contacts" && (
              <>
                <Row label="Contact Person">
                  <Input value={form.contact_person} onChange={set("contact_person")} className="md:w-96" data-testid="cust-contact-person" />
                </Row>
                <Row label="Designation">
                  <Input value={form.contact_designation} onChange={set("contact_designation")} className="md:w-96" />
                </Row>
                <Row label="Alt Email">
                  <Input type="email" value={form.alt_email} onChange={set("alt_email")} className="md:w-96" />
                </Row>
              </>
            )}

            {tab === "commercial" && (
              <>
                <Row label="Industry">
                  <Input value={form.industry} onChange={set("industry")} className="md:w-96" placeholder="Construction, Logistics …" />
                </Row>
                <Row label="Website">
                  <Input value={form.website} onChange={set("website")} className="md:w-96" placeholder="https://…" />
                </Row>
                <Row label="Price per litre (₹)">
                  <Input type="number" step="0.1" value={form.price_per_litre} onChange={set("price_per_litre")} className="md:w-48" />
                </Row>
                <Row label="Credit Limit (₹)">
                  <Input type="number" value={form.credit_limit} onChange={set("credit_limit")} className="md:w-48" />
                </Row>
                <Row label="Credit Days">
                  <Input type="number" value={form.credit_days} onChange={set("credit_days")} className="md:w-48" />
                </Row>
                <Row label="Postpaid enabled">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!form.is_postpaid}
                      onChange={(e) => setVal("is_postpaid", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-primary"
                      data-testid="cust-is-postpaid"
                    />
                    <div className="text-xs text-muted-foreground">
                      When <b>on</b>, this customer can place orders on credit and settle later per the payment terms above. Also honours the credit limit if positive.
                    </div>
                  </div>
                </Row>
                <Row label="Wallet balance (₹)">
                  <Input type="number" value={form.wallet_balance || 0} onChange={set("wallet_balance")} className="md:w-48" data-testid="cust-wallet-balance" />
                </Row>
              </>
            )}

            {tab === "tags" && (
              <Row label="Reporting Tags">
                <Input
                  value={(form.reporting_tags || []).join(", ")}
                  onChange={(e) => setVal("reporting_tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  className="md:w-[32rem]"
                  placeholder="Comma-separated e.g. VIP, Priority, Bulk"
                  data-testid="cust-reporting-tags"
                />
              </Row>
            )}

            {tab === "remarks" && (
              <Row label="Remarks">
                <Textarea
                  rows={4}
                  value={form.remarks}
                  onChange={set("remarks")}
                  className="md:w-[32rem]"
                  placeholder="Internal notes about this customer…"
                  data-testid="cust-remarks"
                />
              </Row>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-border/60 px-6 py-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !(form.display_name || form.company_name || form.first_name || form.last_name) || !form.phone}
            data-testid="btn-save-customer"
          >
            {saving ? "Saving…" : editor.mode === "create" ? "Save" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, required, children }) => (
  <div className="grid grid-cols-1 items-start gap-2 md:grid-cols-4 md:gap-4">
    <Label className="pt-2 text-sm text-muted-foreground md:col-span-1">
      {label}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </Label>
    <div className="md:col-span-3">{children}</div>
  </div>
);
